/**
 * Store: Collectors Club
 * -----------------------------------------------------------------------------
 * Collectors Club state and events.
 */
export default {
  namespaced: true,

  state: () => ({
    customer: {
      isCollector: window.cnvs.customer.isCollector,
    },
    content: {
      article: false,
      blog: false,
      collection: false,
      page: false,
      product: false,
      variant: false,
    },
    membershipProduct: false,
    freeMembership: {
      enabled: false,
      threshold: 0,
      messageContent: {},
    },
  }),

  getters: {

    /**
     * Check if the current product in state is collectors club exclusive and
     * the customer is not a collector
     * @param {Object} state Collectors Club state.
     * @returns Boolean
     */
    getProductCollectorsClubRestricted(state) {
      const isCollectorsClubProduct = Boolean(state.content.product?.limits?.collectors) ||
      Boolean(state.content.product?.limits?.collectorsEarly)

      return isCollectorsClubProduct && !state.customer.isCollector
    },

    /**
     * Get cart items from cart state and ignore Membership product from
     * calculations.
     * @returns {Array} - Filtered array of cart items.
     */
    cartItems(state, _getters, rootState) {
      const cartItems = rootState.cart.response.items.filter((item) => {

        return item?.product_id !== state.membershipProduct?.id
      })

      return cartItems
    },

    /**
     * Calculate cart total based on `cartItems` getter.
     * @returns {Number} - Cart total.
     */
    cartTotal(_state, getters) {
      let totalPrice = 0

      getters.cartItems.forEach((item) => {
        totalPrice += item.line_price
      })

      return totalPrice
    },

    /**
     * Calculate if the free membership threshold has been reached.
     * @returns {Boolean}
     */
    freeMembershipThresholdReached(state, getters) {
      return state.freeMembership.enabled &&
        getters.cartTotal >= state.freeMembership.threshold
    },

    /**
     * Calculate the remaining amount required to reach the free membership
     * threshold
     * @returns {Number} - Amount remaining
     */
    freeMembershipRemaining(state, getters) {
      const remaining = state.freeMembership.threshold - getters.cartTotal
      return Math.max(0, remaining)
    },

    /**
     * Calculate the user's progress towards free membership.
     * - Value is calculated as a percentage from 0 to 100.
     * @returns {Number} - process percentage value
     */
    freeMembershipProgress(state, getters) {
      const progress = getters.cartTotal / state.freeMembership.threshold * 100
      return Math.min(100, progress)
    },

    /**
     * Get the membership content for the free membership threshold content.
     * @returns {Object}
     */
    freeMembershipContent(state) {
      return state.freeMembership.messageContent
    },
  },

  mutations: {
    SET_CONTENT(state, content) {
      state.content = content
    },
    SET_MEMBERSHIP_PRODUCT(state, payload) {
      state.membershipProduct = payload
    },
    SET_FREE_MEMBERSHIP_CONTENT(state, payload) {

      /**
       * Override the enabled prop.
       * - If the enabled setting is true, then set check the threshold is > 0
       * - Otherwise, it's false.
       */
      const enabled = payload.enabled ? payload.threshold > 0 : false
      const modifiedPayload = {
        ...payload,
        enabled,
      }

      state.freeMembership = modifiedPayload
    },
  },

  actions: {
    setContent({ commit }, content) {
      commit('SET_CONTENT', content)
    },
    setMembershipProduct({ commit }, payload) {
      commit('SET_MEMBERSHIP_PRODUCT', payload)
    },
    setFreeMembershipContent({ commit }, payload) {
      commit('SET_FREE_MEMBERSHIP_CONTENT', payload)
    },
  },

  /**
   * TODO:
   * - Persist state for redirection before/after logging in as a collector.
   *  - See existing code for cookie based approach. Use native Vuex setup for
   *    new approach.
   */
}
